interface SliderProps {
  id?: string
  value: number
  min: number
  max: number
  step?: number
  /** Jeder Zwischenwert beim Ziehen (nur anzeigen). */
  onChange: (v: number) => void
  /** Der Wert ist gesetzt: Loslassen, Pfeiltaste losgelassen, Fokus weg. Hier speichern, nicht in onChange. */
  onCommit?: (v: number) => void
  /** Einheit hinter der Zahl („%", „Monate"). */
  unit?: string
  ariaLabel?: string
  disabled?: boolean
  /**
   * Filter-bar look (opt-in, e.g. Vertragszeit-Abgleich): with a `label` the label and the value pill share one line above the track,
   * the track is filled in the accent colour up to the thumb and a scale (`ticks`, default min and max) sits under it.
   * `valueText` is the pill text ("± 15 min", "aus"); without it the pill shows the number and `unit`.
   * Without `label` the slider looks exactly as before.
   */
  label?: string
  valueText?: string
  ticks?: readonly number[]
}

const zahl = (v: number) => v.toLocaleString('de-DE', { maximumFractionDigits: 2 })

/**
 * Schieberegler: natives `<input type="range">` (Tastatur, Screenreader, Touch gratis) in der Akzentfarbe,
 * rechts der Wert als Zahl mit Einheit, darunter Minimum und Maximum. Gespeichert wird über `onCommit`,
 * damit Ziehen nicht bei jedem Pixel eine Anfrage auslöst.
 */
export function Slider({ id, value, min, max, step = 1, onChange, onCommit, unit, ariaLabel, disabled, label, valueText, ticks }: SliderProps) {
  const lies = (t: EventTarget) => Number((t as HTMLInputElement).value)
  const fertig = (t: EventTarget) => onCommit?.(lies(t))
  if (label) {
    const pct = max > min ? ((value - min) / (max - min)) * 100 : 0
    const pill = valueText ?? `${zahl(value)}${unit ? ` ${unit}` : ''}`
    const fieldId = id ?? `slider-${label.replace(/\s+/g, '-').toLowerCase()}`
    return (
      <div className={`flex w-full flex-col gap-1.5 ${disabled ? 'opacity-50' : ''}`}>
        <label htmlFor={fieldId} className="flex min-h-5 items-center justify-between gap-2 text-xs font-semibold tracking-[0.02em] text-on-surface-variant">
          {label}
          <span className="rounded-full bg-primary-container px-2 py-0.5 font-mono text-xs font-medium tracking-normal text-primary">{pill}</span>
        </label>
        <input
          id={fieldId} type="range" min={min} max={max} step={step} value={value} disabled={disabled}
          aria-label={ariaLabel} aria-valuetext={pill}
          onChange={(e) => onChange(lies(e.target))}
          onPointerUp={(e) => fertig(e.target)}
          onKeyUp={(e) => fertig(e.target)}
          onBlur={(e) => fertig(e.target)}
          style={{ background: `linear-gradient(to right, var(--color-primary) ${pct}%, var(--color-surface-container-highest) ${pct}%)` }}
          className="my-1.5 h-1.5 w-full cursor-pointer appearance-none rounded-full outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 disabled:cursor-not-allowed [&::-moz-range-thumb]:size-[18px] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-surface-container-lowest [&::-moz-range-thumb]:shadow-[0_0_0_2px_var(--color-primary),0_2px_6px_rgba(15,23,42,0.25)] [&::-webkit-slider-thumb]:size-[18px] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-surface-container-lowest [&::-webkit-slider-thumb]:shadow-[0_0_0_2px_var(--color-primary),0_2px_6px_rgba(15,23,42,0.25)]"
        />
        <div className="flex justify-between px-[3px] font-mono text-[11px] tabular-nums text-on-surface-variant" aria-hidden="true">
          {(ticks ?? [min, max]).map((t) => <span key={t}>{zahl(t)}</span>)}
        </div>
      </div>
    )
  }
  return (
    <div className={`w-full ${disabled ? 'opacity-50' : ''}`}>
      <div className="flex items-center gap-3">
        <input
          id={id} type="range" min={min} max={max} step={step} value={value} disabled={disabled}
          aria-label={ariaLabel} aria-valuetext={unit ? `${zahl(value)} ${unit}` : undefined}
          onChange={(e) => onChange(lies(e.target))}
          onPointerUp={(e) => fertig(e.target)}
          onKeyUp={(e) => fertig(e.target)}
          onBlur={(e) => fertig(e.target)}
          className="h-1.5 min-w-0 flex-1 cursor-pointer accent-primary disabled:cursor-not-allowed"
        />
        <span className="min-w-[4.5rem] rounded-lg border border-outline-variant/60 bg-surface-container-low px-2 py-1 text-right text-sm font-semibold tabular-nums text-on-surface">
          {zahl(value)}{unit ? <span className="ms-1 text-xs font-normal text-on-surface-variant">{unit}</span> : null}
        </span>
      </div>
      <div className="mt-1 flex justify-between pe-[5.25rem] text-[10px] tabular-nums text-on-surface-variant" aria-hidden="true">
        <span>{zahl(min)}</span>
        <span>{zahl(max)}</span>
      </div>
    </div>
  )
}
